import type {
  DatatableActionDefinition,
  ModalActionDefinition,
  CommandActionDefinition,
  LinkActionDefinition,
} from '../../../types/datatable-types';
import { initFetchModal } from '../modal-fetch';
import { initConfirmButtons } from '../modal-confirm';

/**
 * Executes a primary row action programmatically
 * @param action - The action definition to execute
 * @param rowId - The ID of the row (optional, used for action URL construction)
 */
export async function executePrimaryAction(action: DatatableActionDefinition): Promise<void> {
  switch (action.ActionType) {
    case 0: // Modal
      executeModalAction(action as ModalActionDefinition);
      break;

    case 1: // Command
      await executeCommandAction(action as CommandActionDefinition);
      break;

    case 2: // Link
      executeLinkAction(action as LinkActionDefinition);
      break;
  }
}

/**
 * Opens a modal by programmatically triggering modal-fetch
 */
function executeModalAction(action: ModalActionDefinition): void {
  // Create a temporary hidden button with modal attributes
  const tempButton = document.createElement('button');
  tempButton.className = 'btn-modal';
  tempButton.dataset.bizAction = action.Url;
  tempButton.style.display = 'none';
  document.body.appendChild(tempButton);

  // Initialize modal fetch on the button
  initFetchModal(document.body);

  // Trigger click
  tempButton.click();

  // Cleanup after modal opens
  setTimeout(() => {
    document.body.removeChild(tempButton);
  }, 100);
}

/**
 * Executes a command action (POST request with optional confirmation)
 */
async function executeCommandAction(action: CommandActionDefinition): Promise<void> {
  if (action.Confirmation.RequiresConfirmation) {
    // Create a temporary button with confirmation attributes
    const tempButton = document.createElement('button');
    tempButton.className = 'btn-confirm';
    tempButton.dataset.bizMessage = action.Confirmation.ConfirmationMessage || '';
    tempButton.dataset.bizAction = action.Url;
    tempButton.dataset.bizMethod = action.HttpMethod;
    tempButton.style.display = 'none';
    document.body.appendChild(tempButton);

    // Initialize confirm modal on the button
    initConfirmButtons([tempButton]);

    // Trigger click to show confirmation modal
    tempButton.click();

    // Cleanup after modal interaction
    setTimeout(() => {
      document.body.removeChild(tempButton);
    }, 100);
  } else {
    // Execute POST request without confirmation
    try {
      const response = await fetch(action.Url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Check if response contains redirect instruction
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          if (data.redirectUrl) {
            window.location.href = data.redirectUrl;
            return;
          }
        }

        // Default: reload page
        window.location.reload();
      } else {
        // eslint-disable-next-line no-console
        console.error('Action failed:', response.statusText);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Command action error:', error);
    }
  }
}

/**
 * Executes a link action (navigation)
 */
function executeLinkAction(action: LinkActionDefinition): void {
  if (action.OpenInNewTab) {
    window.open(action.Url, '_blank', 'noopener,noreferrer');
  } else {
    window.location.href = action.Url;
  }
}
